<template>
  <section
    :key="`pickup-${$route.params.id}`"
    class="pickup-preview-wrapper"
  >
    <!-- Loading State -->
    <div
      v-if="isLoading"
      class="loading-container d-print-none"
    >
      <b-spinner
        variant="primary"
        class="loading-spinner"
      />
      <p class="mt-3 text-muted">
        Chargement du pickup...
      </p>
    </div>

    <!-- Main Content -->
    <div
      v-if="!isLoading"
      :key="`content-${pickup.id}`"
    >
      <!-- Top Action Bar -->
      <div class="top-action-bar mb-4 d-print-none">
        <b-row class="align-items-center">
          <b-col
            cols="12"
            md="6"
          >
            <h2 class="page-title mb-0">
              <feather-icon
                icon="TruckIcon"
                size="24"
                class="mr-2"
              />
              Collecte N° {{ pickup.id }}
            </h2>
          </b-col>
          <b-col
            cols="12"
            md="6"
            class="text-md-right"
          >
            <div class="action-buttons-top">
              <b-button
                v-if="canConfirm(pickup)"
                v-ripple.400="'rgba(40, 167, 69, 0.15)'"
                variant="success"
                class="action-btn-top mr-2"
                @click="showConfirmDialog"
              >
                <feather-icon
                  icon="CheckCircleIcon"
                  class="mr-1"
                  size="16"
                />
                <span class="align-middle">Confirmer</span>
              </b-button>

              <b-dropdown
                v-ripple.400="'rgba(10, 38, 52, 0.15)'"
                variant="outline-primary"
                class="action-btn-top mr-2"
                :disabled="isLoading"
                text="Imprimer"
                right
              >
                <template #button-content>
                  <feather-icon
                    icon="PrinterIcon"
                    size="16"
                    class="mr-2"
                  />
                  Imprimer
                  <feather-icon
                    icon="ChevronDownIcon"
                    size="14"
                    class="ml-1"
                  />
                </template>

                <b-dropdown-item
                  v-b-tooltip.hover.right="'Imprimer une version optimisée en noir et blanc, sans couleurs et avec un espacement réduit.'"
                  @click="printPickupBW"
                >
                  <feather-icon
                    icon="PrinterIcon"
                    size="16"
                    class="mr-2"
                  />
                  Version noir et blanc
                </b-dropdown-item>

                <b-dropdown-item
                  v-b-tooltip.hover.right="'Imprimer le pickup en couleur avec tous les éléments graphiques.'"
                  @click="printPickup"
                >
                  <feather-icon
                    icon="PrinterIcon"
                    size="16"
                    class="mr-2"
                  />
                  Version couleur
                </b-dropdown-item>
              </b-dropdown>

              <b-button
                v-ripple.400="'rgba(113, 102, 240, 0.15)'"
                variant="outline-primary"
                class="action-btn-top"
                @click="$router.go(-1)"
              >
                <feather-icon
                  icon="ArrowLeftIcon"
                  class="mr-1"
                  size="16"
                />
                <span class="align-middle">Retour</span>
              </b-button>

              <!-- Quick Stats - Updated to use API summary -->
              <div class="quick-stats d-inline-flex ml-3">
                <div class="stat-item-inline primary">
                  <feather-icon
                    icon="PackageIcon"
                    size="14"
                  />
                  <span>{{ summaryData.order_count }}</span>
                </div>
                <div class="stat-item-inline success">
                  <feather-icon
                    icon="DollarSignIcon"
                    size="14"
                  />
                  <span>{{ formatCurrency(summaryData.total_price_ttc) }}</span>
                </div>
                <div class="stat-item-inline warning">
                  <feather-icon
                    icon="MapPinIcon"
                    size="14"
                  />
                  <span>{{ summaryData.unique_governorates_count }}</span>
                </div>
              </div>
            </div>
          </b-col>
        </b-row>
      </div>

      <!-- Pickup Content - This is what gets printed -->
      <div
        id="pickup-content"
        class="pickup-content"
      >
        <!-- Full Width Pickup Document -->
        <b-card class="pickup-card shadow-sm">
          <!-- Header Section -->
          <div class="pickup-header">
            <div class="d-flex justify-content-between align-items-center">
              <div class="company-logo">
                <brand-logo class="logo-img" />
              </div>
              <div class="text-right">
                <h1 class="pickup-title">
                  Collecte N°
                  <span class="pickup-number">{{ pickup.id }}</span>
                </h1>
                <div class="pickup-date">
                  <feather-icon
                    icon="CalendarIcon"
                    size="16"
                    class="mr-1"
                  />
                  {{ formatDateTime(pickup.created_at) }}
                </div>
              </div>
            </div>
          </div>

          <!-- Company Information & Summary - Two Columns in a Row -->
          <!-- Company/Supplier Info Section -->
          <div class="company-info-section mb-4">
            <b-row>
              <!-- Our Company -->
              <b-col
                md="6"
                class="mb-3 mb-md-0"
              >
                <div class="info-card company-card h-100">
                  <div class="card-header-custom">
                    <feather-icon
                      icon="HomeIcon"
                      size="18"
                      class="mr-2"
                    />
                    <span class="font-weight-bold">Notre Société</span>
                  </div>
                  <div class="card-body-custom mt-2">
                    <h5 class="mb-2 font-weight-bold">
                      {{ company.name || 'B2C Delivery' }}
                    </h5>
                    <p class="mb-1 text-muted small">
                      {{ company.tagline || 'Société de livraison des colis' }}
                    </p>
                    <p
                      v-if="company.tax_id"
                      class="mb-3 text-muted small"
                    >
                      Matricule fiscale: {{ company.tax_id }}
                    </p>
                    <div class="d-flex justify-content-between">
                      <span class="text-muted small">Adresse: {{ company.address || 'Hammamet' }}</span>
                    </div>
                    <div class="d-flex justify-content-between">
                      <span class="text-muted small">Téléphone: {{ company.phone || '55030005' }}</span>
                    </div>
                  </div>
                </div>
              </b-col>
              <!-- Supplier -->
              <b-col md="6">
                <div class="info-card supplier-card h-100">
                  <div class="card-header-custom">
                    <feather-icon
                      icon="UserIcon"
                      size="18"
                      class="mr-2"
                    />
                    <span class="font-weight-bold">Fournisseur</span>
                  </div>
                  <div class="card-body-custom mt-2">
                    <h5 class="mb-2 font-weight-bold">
                      {{ pickup.supplier ? pickup.supplier.username : 'N/A' }}
                    </h5>
                    <p
                      v-if="pickup.supplier && pickup.supplier.tax_id"
                      class="mb-2 text-muted small"
                    >
                      Matricule fiscale: {{ pickup.supplier.tax_id }}
                    </p>
                    <div class="d-flex justify-content-between mb-1">
                      <span class="text-muted small">Adresse:</span>
                      <span class="text-dark small font-weight-bold">{{ pickup.supplier ? (pickup.supplier.address || 'Manaret Hammamet en face Kiosque Total') : 'N/A' }}</span>
                    </div>
                    <div class="d-flex justify-content-between">
                      <span class="text-muted small">Téléphone:</span>
                      <span class="text-dark small font-weight-bold">{{ pickup.supplier ? pickup.supplier.phone : 'N/A' }}</span>
                    </div>
                  </div>
                </div>
              </b-col>
            </b-row>

            <!-- Collection Status Row - Only show if assigned -->
            <b-row
              v-if="pickup.assigned_to || pickup.status !== 'pending'"
              class="mt-3"
            >
              <b-col md="6">
                <div class="info-card driver-card h-100">
                  <div class="card-header-custom">
                    <feather-icon
                      icon="TruckIcon"
                      size="18"
                      class="mr-2"
                    />
                    <span class="font-weight-bold">Livreur assigné</span>
                  </div>
                  <div class="card-body-custom mt-2">
                    <div v-if="pickup.assigned_to">
                      <div class="d-flex align-items-center mb-2">
                        <h5 class="mb-0 font-weight-bold">
                          {{ pickup.assigned_to.username }}
                        </h5>
                        <b-badge
                          v-if="canAssignPermission && canAssignPickup"
                          variant="light-primary"
                          class="ml-2 cursor-pointer"
                          @click="openAssignModal"
                        >
                          Changer
                        </b-badge>
                      </div>
                      <div class="d-flex justify-content-between mb-1">
                        <span class="text-muted small">Téléphone:</span>
                        <span class="text-dark small font-weight-bold">{{ pickup.assigned_to.phone || 'N/A' }}</span>
                      </div>
                      <div
                        v-if="pickup.assigned_at"
                        class="d-flex justify-content-between"
                      >
                        <span class="text-muted small">Assigné le:</span>
                        <span class="text-dark small font-weight-bold">{{ formatDateTime(pickup.assigned_at) }}</span>
                      </div>
                      <div
                        v-if="pickup.accepted_at"
                        class="d-flex justify-content-between mb-1"
                      >
                        <span class="text-muted small">Accepté le:</span>
                        <span class="text-dark small font-weight-bold">{{ formatDateTime(pickup.accepted_at) }}</span>
                      </div>
                      <div
                        v-if="canRevokeAcceptance"
                        class="mt-2"
                      >
                        <b-button
                          size="sm"
                          variant="outline-danger"
                          :disabled="isRevokingAcceptance"
                          @click="revokeAcceptance"
                        >
                          <b-spinner
                            v-if="isRevokingAcceptance"
                            small
                            class="mr-1"
                          />
                          Révoquer l'acceptation
                        </b-button>
                      </div>
                      <div
                        v-if="canAcceptPickup || canStartPickup"
                        class="mt-2 d-flex flex-wrap"
                      >
                        <b-button
                          v-if="canAcceptPickup"
                          size="sm"
                          variant="primary"
                          class="mr-1 mb-1"
                          :disabled="isAccepting"
                          @click="acceptPickup"
                        >
                          <b-spinner
                            v-if="isAccepting"
                            small
                            class="mr-1"
                          />
                          Accepter la collecte
                        </b-button>
                        <b-button
                          v-if="canStartPickup"
                          size="sm"
                          variant="success"
                          class="mb-1"
                          :disabled="isStarting"
                          @click="startPickup"
                        >
                          <b-spinner
                            v-if="isStarting"
                            small
                            class="mr-1"
                          />
                          Démarrer la collecte
                        </b-button>
                      </div>
                    </div>
                    <div
                      v-else
                      class="text-muted"
                    >
                      <div>Non assigné</div>
                      <b-button
                        v-if="canAssignPermission && canAssignPickup"
                        size="sm"
                        variant="outline-primary"
                        class="mt-2"
                        @click="openAssignModal"
                      >
                        Assigner un livreur
                      </b-button>
                    </div>
                  </div>
                </div>
              </b-col>
              <b-col md="6">
                <div class="info-card status-card h-100">
                  <div class="card-header-custom">
                    <feather-icon
                      icon="ActivityIcon"
                      size="18"
                      class="mr-2"
                    />
                    <span class="font-weight-bold">Statut de collecte</span>
                  </div>
                  <div class="card-body-custom mt-2">
                    <div class="d-flex justify-content-between align-items-center mb-2">
                      <span class="text-muted small">Statut:</span>
                      <b-badge :variant="getStatusVariant(pickup.status)">
                        {{ getStatusText(pickup.status) }}
                      </b-badge>
                    </div>
                    <div
                      v-if="pickup.scanned_orders_count !== undefined"
                      class="d-flex justify-content-between mb-1"
                    >
                      <span class="text-muted small">Progression:</span>
                      <span class="text-dark small font-weight-bold">{{ pickup.scanned_orders_count }}/{{ pickup.expected_orders_count }} scannées</span>
                    </div>
                    <div
                      v-if="pickup.started_at"
                      class="d-flex justify-content-between mb-1"
                    >
                      <span class="text-muted small">Démarré le:</span>
                      <span class="text-dark small font-weight-bold">{{ formatDateTime(pickup.started_at) }}</span>
                    </div>
                    <div
                      v-if="pickup.collected_at"
                      class="d-flex justify-content-between"
                    >
                      <span class="text-muted small">Collecté le:</span>
                      <span class="text-dark small font-weight-bold">{{ formatDateTime(pickup.collected_at) }}</span>
                    </div>
                  </div>
                </div>
              </b-col>
            </b-row>
          </div>

          <!-- Summary Table - Updated to use API summary -->
          <div class="summary-section">
            <h5 class="section-title">
              <feather-icon
                icon="BarChartIcon"
                size="20"
                class="mr-2"
              />
              Récapitulatif détaillé de la collecte
            </h5>
            <div class="summary-table-container">
              <div class="table-responsive">
                <table class="modern-summary-table">
                  <thead>
                    <tr>
                      <th>Nombre d’expéditions</th>
                      <th>Prix moyen</th>
                      <th>Livraison moyenne</th>
                      <th>Montant total</th>
                      <th>Total Livraison</th>
                      <th>Gouvernorats</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>
                        <b-badge
                          variant="info"
                          class="stat-badge"
                        >
                          {{ summaryData.order_count }}
                        </b-badge>
                      </td>
                      <td class="amount">
                        {{ formatCurrency(summaryData.average_order_price) }}
                      </td>
                      <td class="amount">
                        {{ formatCurrency(summaryData.average_delivery_price) }}
                      </td>
                      <td class="amount total-amount">
                        {{ formatCurrency(summaryData.total_price_ttc) }}
                      </td>
                      <td class="amount">
                        {{ formatCurrency(summaryData.total_price_delivery) }}
                      </td>
                      <td>
                        <b-badge
                          variant="warning"
                          class="stat-badge"
                        >
                          {{ summaryData.unique_governorates_count }}
                        </b-badge>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <!-- Orders List -->
          <div class="orders-section">
            <div class="section-header">
              <h5 class="section-title">
                <feather-icon
                  icon="ListIcon"
                  size="20"
                  class="mr-2"
                />
                Liste des Commandes
                <b-badge
                  variant="primary"
                  class="ml-2"
                >
                  {{ summaryData.order_count }}
                </b-badge>
              </h5>
            </div>

            <div
              v-if="pickup.orders && pickup.orders.length > 0"
              class="table-container"
            >
              <b-table-lite
                responsive
                class="modern-orders-table"
                :items="pickup.orders"
                :fields="[
                  { key: 'id', label: 'N° Commande' },
                  { key: 'name', label: 'Client' },
                  { key: 'phone', label: 'Téléphone' },
                  { key: 'location', label: 'Localisation' },
                  { key: 'price_ttc', label: 'Prix TTC' },
                  { key: 'price_delivery', label: 'Livraison' }
                ]"
                striped
                hover
              >
                <template #cell(id)="data">
                  <div class="order-code">
                    <strong>#{{ data.item.display_cross_tenant_barcode && data.item.cross_tenant_barcode ? data.item.cross_tenant_barcode : data.item.id }}</strong>
                  </div>
                </template>
                <template #cell(phone)="data">
                  <div class="phone-info">
                    <div class="phone-primary">
                      {{ data.item.phone1 }}
                    </div>
                    <div
                      v-if="data.item.phone2"
                      class="phone-secondary"
                    >
                      {{ data.item.phone2 }}
                    </div>
                  </div>
                </template>
                <template #cell(location)="data">
                  <div class="location-info">
                    <div
                      v-if="data.item.governorate"
                      class="location-item governorate"
                    >
                      {{ data.item.governorate }}
                    </div>
                    <div
                      v-if="data.item.delegation"
                      class="location-item delegation"
                    >
                      {{ data.item.delegation }}
                    </div>
                    <div
                      v-if="data.item.locality"
                      class="location-item locality"
                    >
                      {{ data.item.locality }}
                    </div>
                  </div>
                </template>
                <template #cell(price_ttc)="data">
                  <span class="amount">{{ formatCurrency(data.item.price_ttc) }}</span>
                </template>
                <template #cell(price_delivery)="data">
                  <span class="amount">{{ formatCurrency(data.item.price_delivery) }}</span>
                </template>
              </b-table-lite>
            </div>

            <div
              v-else
              class="no-orders-state"
            >
              <div class="empty-icon">
                <feather-icon
                  icon="InboxIcon"
                  size="48"
                  class="d-print-none"
                />
              </div>
              <p class="empty-message">
                Aucune commande dans ce pickup.
              </p>
            </div>
          </div>

          <!-- Additional Analytics - Updated to use API summary -->
          <div class="analytics-section">
            <h5 class="section-title">
              <feather-icon
                icon="TrendingUpIcon"
                size="20"
                class="mr-2"
              />
              Analyses et Statistiques
            </h5>
            <div class="analytics-content">
              <div class="analytics-grid">
                <div class="analytics-item">
                  <span class="analytics-label">Prix moyen par commande:</span>
                  <span class="analytics-value">{{ formatCurrency(summaryData.average_order_price) }}</span>
                </div>
                <div class="analytics-item">
                  <span class="analytics-label">Livraison moyenne:</span>
                  <span class="analytics-value">{{ formatCurrency(summaryData.average_delivery_price) }}</span>
                </div>
                <div class="analytics-item">
                  <span class="analytics-label">Gouvernorats couverts:</span>
                  <span class="analytics-value">{{ summaryData.unique_governorates.join(', ') || 'Aucun' }}</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Notes Section -->
          <div
            v-if="pickup.notes"
            class="notes-section"
          >
            <h5 class="section-title">
              <feather-icon
                icon="FileTextIcon"
                size="20"
                class="mr-2"
              />
              Notes et Observations
            </h5>
            <div class="notes-content">
              {{ pickup.notes }}
            </div>
          </div>

          <!-- Signature Section -->
          <div class="signature-section">
            <b-row>
              <b-col
                cols="12"
                md="6"
              >
                <div class="signature-box">
                  <h6 class="signature-title">
                    Signature du Fournisseur
                  </h6>
                  <div class="signature-line" />
                  <div class="signature-date">
                    Date: ________________
                  </div>
                </div>
              </b-col>
              <b-col
                cols="12"
                md="6"
              >
                <div class="signature-box">
                  <h6 class="signature-title">
                    {{ company.signature_label_company || `${$branding.company.signatureLabel} ${$branding.company.name}` }}
                  </h6>
                  <div class="signature-line" />
                </div>
              </b-col>
            </b-row>
          </div>

          <!-- Document Footer -->
          <div class="document-footer">
            <div class="row align-items-end">
              <div class="col-md-12 text-center">
                <div class="print-info">
                  <p class="print-date">
                    <strong>Document imprimé le:</strong> {{ formatDateTime(new Date()) }}
                  </p>
                  <p class="print-note">
                    Document généré automatiquement par le système {{ $branding.company.name }}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </b-card>
      </div>
    </div>

    <!-- Driver Assignment Modal -->
    <b-modal
      v-model="showAssignModal"
      title="Assigner un livreur"
      title-class="text-primary"
      ok-title="Assigner"
      ok-variant="primary"
      cancel-title="Annuler"
      cancel-variant="outline-secondary"
      :ok-disabled="!selectedDriver || isAssigning || isSameAssigneeSelected"
      :cancel-disabled="isAssigning"
      no-close-on-backdrop
      @ok.prevent="assignDriver"
      @cancel="closeAssignModal"
    >
      <div>
        <div class="bg-light p-3 rounded mb-3">
          <div class="d-flex justify-content-between align-items-center mb-2">
            <span class="font-weight-bold">Pickup #{{ pickup.id }}</span>
            <b-badge :variant="getStatusVariant(pickup.status)">
              {{ getStatusText(pickup.status) }}
            </b-badge>
          </div>
          <div class="d-flex justify-content-between">
            <span>Fournisseur:</span>
            <span class="font-weight-bold">{{ (pickup.supplier && pickup.supplier.username) || 'N/A' }}</span>
          </div>
          <div class="d-flex justify-content-between">
            <span>Commandes:</span>
            <span class="font-weight-bold">{{ pickup.orders_count || 0 }}</span>
          </div>
        </div>

        <div class="form-group">
          <label
            for="driver-select"
            class="font-weight-bold"
          >Sélectionner un livreur</label>
          <div
            v-if="isLoadingDrivers"
            class="text-center py-3"
          >
            <b-spinner
              variant="primary"
              small
            />
            <span class="ml-2">Chargement des livreurs...</span>
          </div>
          <select
            v-else
            id="driver-select"
            v-model="selectedDriver"
            class="form-control"
          >
            <option
              :value="null"
              disabled
            >
              -- Sélectionner un livreur/pickupeur --
            </option>
            <option
              v-for="driver in drivers"
              :key="driver.id"
              :value="driver"
            >
              {{ driver.username }} - {{ getRoleLabel(driver.role) }} ({{ driver.phone || 'N/A' }})
              <span v-if="isCurrentAssigneeOption(driver)"> (Déjà assigné)</span>
            </option>
          </select>
        </div>

        <div
          v-if="isAssigning"
          class="text-center mt-3"
        >
          <b-spinner
            variant="primary"
            small
          />
          <span class="ml-2">Assignation en cours...</span>
        </div>
      </div>
    </b-modal>

    <!-- Confirmation Modal -->
    <b-modal
      v-model="showConfirmationModal"
      title="Confirmer le Pickup"
      title-class="text-danger"
      ok-title="Confirmer"
      ok-variant="success"
      cancel-title="Annuler"
      cancel-variant="outline-secondary"
      @ok="confirmPickup"
      @cancel="cancelConfirmation"
    >
      <div class="text-center">
        <feather-icon
          icon="AlertTriangleIcon"
          size="48"
          class="text-warning mb-3"
        />
        <h5 class="mb-3">
          Êtes-vous sûr de vouloir confirmer ce pickup ?
        </h5>
        <p class="text-muted">
          Cette action confirmera le pickup #{{ pickup.id }} et ne pourra pas être annulée.
        </p>
        <div
          v-if="isConfirming"
          class="mt-3"
        >
          <b-spinner
            variant="success"
            small
            class="mr-2"
          />
          <span>Confirmation en cours...</span>
        </div>
      </div>
    </b-modal>
  </section>
</template>

<script>
import axios from '@axios'
import { getUserData } from '@/auth/utils'
import storeAuth from '@/store/store'
import {
  BButton, BSpinner, BBadge, BModal, BCard, BTableLite, BDropdown, BDropdownItem, BRow, BCol,
} from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'

export default {
  name: 'PickupShow',
  components: {
    BButton,
    BSpinner,
    BBadge,
    BModal,
    BCard,
    BTableLite,
    BDropdown,
    BDropdownItem,
    BRow,
    BCol,
  },
  directives: {
    Ripple,
  },
  data() {
    return {
      pickup: {
        orders: [],
        supplier: null,
        total_price_ttc: 0,
        total_price_delivery: 0,
        status: '',
        status_display: '',
        id: null,
        created_at: null,
        notes: null,
        summary: {
          order_count: 0,
          average_order_price: 0,
          average_delivery_price: 0,
          total_price_ttc: 0,
          total_price_delivery: 0,
          unique_governorates: [],
          unique_governorates_count: 0,
        },
      },
      isLoading: true,
      showConfirmationModal: false,
      isConfirming: false,
      isRevokingAcceptance: false,
      isAccepting: false,
      isStarting: false,
      showAssignModal: false,
      selectedDriver: null,
      drivers: [],
      isAssigning: false,
      isLoadingDrivers: false,
      user: getUserData() || {},
      componentKey: 0,
      company: storeAuth.state.company || {},
    }
  },
  computed: {
    isDriver() {
      return ['delivery_man', 'pickupeur'].includes(this.user.role)
    },
    isAssignedDriver() {
      return this.isDriver && this.pickup?.assigned_to?.id === this.user.id
    },
    // Updated to use API summary data
    summaryData() {
      return this.pickup.summary || {
        order_count: 0,
        average_order_price: 0,
        average_delivery_price: 0,
        total_price_ttc: 0,
        total_price_delivery: 0,
        unique_governorates: [],
        unique_governorates_count: 0,
      }
    },
    canRevokeAcceptance() {
      return (this.user.role === 'admin' || this.user.role === 'superadmin')
        && this.pickup.status === 'accepted'
    },
    canAssignPermission() {
      return this.user.role === 'admin' || this.user.role === 'manager'
    },
    canAssignPickup() {
      if (!this.pickup) return false
      const isAdmin = ['admin', 'superadmin', 'admin_developer'].includes(this.user.role)
      if (this.pickup.status === 'accepted') {
        return isAdmin
      }
      return ['pending', 'confirmed', 'assigned'].includes(this.pickup.status)
    },
    isSameAssigneeSelected() {
      return !!this.pickup?.assigned_to?.id
        && this.selectedDriver?.id === this.pickup.assigned_to.id
    },
    canAcceptPickup() {
      return this.isAssignedDriver && this.pickup.status === 'assigned'
    },
    canStartPickup() {
      return this.isAssignedDriver && this.pickup.status === 'accepted'
    },
  },
  watch: {
    '$route.params.id': {
      handler(newId, oldId) {
        if (newId !== oldId) {
          this.resetComponent()
          this.fetchPickupDetails()
        }
      },
      immediate: false,
    },
  },
  async created() {
    // Use created instead of mounted for better lifecycle management
    await this.fetchPickupDetails()
  },
  async mounted() {
    // Force styles recalculation on mount
    this.$nextTick(() => {
      this.forceStylesRefresh()
    })
  },
  methods: {
    resetComponent() {
      // Reset component state on navigation
      this.componentKey += 1
      this.pickup = {
        orders: [],
        supplier: null,
        total_price_ttc: 0,
        total_price_delivery: 0,
        status: '',
        status_display: '',
        id: null,
        created_at: null,
        notes: null,
        summary: {
          order_count: 0,
          average_order_price: 0,
          average_delivery_price: 0,
          total_price_ttc: 0,
          total_price_delivery: 0,
          unique_governorates: [],
          unique_governorates_count: 0,
        },
      }
      this.isLoading = true
      this.showConfirmationModal = false
      this.isConfirming = false
    },

    forceStylesRefresh() {
      // Force CSS recalculation by toggling a class
      const element = this.$el
      if (element) {
        element.style.visibility = 'hidden'
        // eslint-disable-next-line no-unused-expressions
        element.offsetHeight // Trigger reflow
        element.style.visibility = 'visible'
      }
    },

    async fetchPickupDetails() {
      try {
        this.isLoading = true
        const pickupId = this.$route.params.id

        // Clear existing data first
        this.pickup = {
          orders: [],
          supplier: null,
          total_price_ttc: 0,
          total_price_delivery: 0,
          status: '',
          status_display: '',
          id: null,
          created_at: null,
          notes: null,
          summary: {
            order_count: 0,
            average_order_price: 0,
            average_delivery_price: 0,
            total_price_ttc: 0,
            total_price_delivery: 0,
            unique_governorates: [],
            unique_governorates_count: 0,
          },
        }

        const response = await axios.get(`/api/pickups/detail/${pickupId}/`)

        // Ensure reactive assignment with proper summary fallback
        this.pickup = {
          ...this.pickup,
          ...response.data,
          orders: response.data.orders || [],
          summary: response.data.summary || {
            order_count: 0,
            average_order_price: 0,
            average_delivery_price: 0,
            total_price_ttc: 0,
            total_price_delivery: 0,
            unique_governorates: [],
            unique_governorates_count: 0,
          },
        }

        // Force reactivity update
        await this.$nextTick()
        this.forceStylesRefresh()
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error fetching pickup details:', error)
        this.$bvToast.toast('Erreur lors du chargement des données du pickup', {
          title: 'Erreur',
          variant: 'danger',
          solid: true,
        })
      } finally {
        this.isLoading = false
      }
    },

    async revokeAcceptance() {
      if (!this.pickup?.id) return
      const confirmed = await this.$bvModal.msgBoxConfirm(
        "Révoquer l'acceptation de ce pickup ?",
        {
          title: 'Confirmation',
          okTitle: 'Révoquer',
          cancelTitle: 'Annuler',
          okVariant: 'danger',
          centered: true,
        },
      )
      if (!confirmed) return

      this.isRevokingAcceptance = true
      try {
        await axios.post(`/api/pickups/${this.pickup.id}/revoke-acceptance/`)
        this.$bvToast.toast('Acceptation révoquée', {
          title: 'Succès',
          variant: 'success',
          solid: true,
        })
        await this.fetchPickupDetails()
      } catch (error) {
        this.$bvToast.toast(error.response?.data?.error || 'Erreur lors de la révocation', {
          title: 'Erreur',
          variant: 'danger',
          solid: true,
        })
      } finally {
        this.isRevokingAcceptance = false
      }
    },

    async acceptPickup() {
      if (!this.pickup?.id || this.isAccepting) return
      const confirmed = await this.$bvModal.msgBoxConfirm(
        'Accepter cette collecte ?',
        {
          title: 'Confirmation',
          okTitle: 'Accepter',
          cancelTitle: 'Annuler',
          okVariant: 'primary',
          centered: true,
        },
      )
      if (!confirmed) return

      this.isAccepting = true
      try {
        await axios.post(`/api/pickups/${this.pickup.id}/accept/`)
        this.$bvToast.toast('Collecte acceptée', {
          title: 'Succès',
          variant: 'success',
          solid: true,
        })
        await this.fetchPickupDetails()
      } catch (error) {
        this.$bvToast.toast(error.response?.data?.error || 'Erreur lors de l\'acceptation', {
          title: 'Erreur',
          variant: 'danger',
          solid: true,
        })
      } finally {
        this.isAccepting = false
      }
    },

    async startPickup() {
      if (!this.pickup?.id || this.isStarting) return
      const confirmed = await this.$bvModal.msgBoxConfirm(
        'Démarrer la collecte ? Le statut passera à "En cours".',
        {
          title: 'Confirmation',
          okTitle: 'Démarrer',
          cancelTitle: 'Annuler',
          okVariant: 'success',
          centered: true,
        },
      )
      if (!confirmed) return

      this.isStarting = true
      try {
        await axios.post(`/api/pickups/${this.pickup.id}/start/`)
        this.$bvToast.toast('Collecte démarrée', {
          title: 'Succès',
          variant: 'success',
          solid: true,
        })
        await this.fetchPickupDetails()
      } catch (error) {
        this.$bvToast.toast(error.response?.data?.error || 'Erreur lors du démarrage', {
          title: 'Erreur',
          variant: 'danger',
          solid: true,
        })
      } finally {
        this.isStarting = false
      }
    },

    async openAssignModal() {
      this.selectedDriver = this.pickup?.assigned_to || null
      this.showAssignModal = true
      await this.fetchDrivers()
      if (this.pickup?.assigned_to?.id) {
        const matchedDriver = this.drivers.find(driver => driver.id === this.pickup.assigned_to.id)
        this.selectedDriver = matchedDriver || this.selectedDriver
      }
    },

    closeAssignModal() {
      this.showAssignModal = false
      this.selectedDriver = null
    },

    async fetchDrivers() {
      this.isLoadingDrivers = true
      try {
        const { data } = await axios.get('/api/auth/users/assignable-pickup-drivers/')
        this.drivers = Array.isArray(data) ? data : data.results || []
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error fetching drivers:', error)
        this.$bvToast.toast('Erreur lors du chargement des livreurs', {
          title: 'Erreur',
          variant: 'danger',
          solid: true,
        })
      } finally {
        this.isLoadingDrivers = false
      }
    },

    async assignDriver() {
      if (!this.pickup?.id || !this.selectedDriver) return

      this.isAssigning = true
      try {
        await axios.post(`/api/pickups/${this.pickup.id}/assign/`, {
          driver_id: this.selectedDriver.id,
        })
        this.$bvToast.toast(`Pickup #${this.pickup.id} assigné à ${this.selectedDriver.username}`, {
          title: 'Succès',
          variant: 'success',
          solid: true,
        })
        this.closeAssignModal()
        await this.fetchPickupDetails()
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error assigning pickup:', error)
        this.$bvToast.toast(error.response?.data?.message || 'Erreur lors de l\'assignation', {
          title: 'Erreur',
          variant: 'danger',
          solid: true,
        })
      } finally {
        this.isAssigning = false
      }
    },

    getStatusVariant(status) {
      const variants = {
        pending: 'warning',
        confirmed: 'info',
        assigned: 'primary',
        accepted: 'primary',
        in_progress: 'info',
        collected: 'success',
        partial: 'warning',
        cancelled: 'secondary',
      }
      return variants[status] || 'secondary'
    },

    getStatusText(status) {
      const texts = {
        pending: 'En attente',
        confirmed: 'Confirmé',
        assigned: 'Assigné',
        accepted: 'Accepté',
        in_progress: 'En cours',
        collected: 'Collecté',
        partial: 'Partiel',
        cancelled: 'Annulé',
      }
      return texts[status] || 'Inconnu'
    },

    getRoleLabel(role) {
      const labels = {
        delivery_man: 'Livreur',
        pickupeur: 'Pickupeur',
        admin: 'Admin',
        manager: 'Manager',
        supplier: 'Fournisseur',
      }
      return labels[role] || role || 'N/A'
    },

    isCurrentAssigneeOption(driver) {
      return !!this.pickup?.assigned_to?.id
        && driver?.id === this.pickup.assigned_to.id
    },

    getOrderStatusText(status) {
      const texts = {
        0: 'En attente',
        15: 'En attente de collecte',
        1: 'En pickup',
        2: 'Au dépôt',
        3: 'En cours',
        4: 'Livré',
        5: 'Retour',
      }
      return texts[status] || 'Inconnu'
    },

    formatCurrency(amount) {
      return new Intl.NumberFormat('fr-TN', {
        style: 'currency',
        currency: 'TND',
        minimumFractionDigits: 3,
      }).format(amount || 0)
    },

    formatDate(date) {
      return new Date(date).toLocaleDateString('fr-FR', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    },

    formatDateTime(date) {
      return new Date(date).toLocaleString('fr-FR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      })
    },

    printPickup() {
      // Enhanced color print styles with better page break handling
      const printCSS = `
        <style id="print-styles">
          @media print {
            html, body {
              margin: 0 !important;
              padding: 0 !important;
              font-family: Arial, sans-serif;
              min-height: 0 !important;
              height: auto !important;
            }
            .pickup-preview-wrapper {
              margin: 0 !important;
              padding: 0 !important;
              min-height: 0 !important;
              height: auto !important;
            }
            .pickup-content {
              margin: 0 !important;
              padding: 0 !important;
            }
            body * { visibility: hidden; }
            #pickup-content, #pickup-content * { visibility: visible; }
            #pickup-content { 
              position: absolute; 
              left: 0; 
              top: 0; 
              width: 100%;
              font-family: Arial, sans-serif;
              font-size: 11px;
              line-height: 1.3;
              color: black;
            }
            .d-print-none, .feather-icon, .no-print { display: none !important; }
            .pickup-header { 
              margin-bottom: 15px; 
              padding: 15px;
              background: #fff5f5 !important;
              color: black !important;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
              page-break-inside: avoid;
              page-break-after: avoid;
            }
            .logo-img {
              max-width: 100px !important;
              height: auto !important;
            }
            .company-name { 
              font-size: 20px; 
              font-weight: bold; 
              color: white !important;
              margin-bottom: 5px;
            }
            .document-title { 
              font-size: 16px; 
              font-weight: bold; 
              color: black !important;
              margin-bottom: 3px;
            }
            .pickup-header .logo-img { display: block !important; filter: none !important; }
            .company-tagline {
              color: #b8d4e3 !important;
              font-size: 10px;
            }
            .pickup-title { 
              font-size: 18px; 
              font-weight: bold; 
              color: white !important;
              margin-bottom: 5px;
              padding-right: 30px;
            }
            .pickup-number {
              color: #ED1919 !important;
            }
            .pickup-date {
              color: white !important;
              font-size: 11px;
              padding-right: 30px;
            }
            .status-container {
               padding-right: 30px;
            }
            .section-title { 
              font-size: 13px; 
              font-weight: bold; 
              border-bottom: 2px solid #0A2634; 
              margin: 12px 0 8px 0; 
              padding-bottom: 3px;
              color: black !important;
              page-break-after: avoid;
            }
            .row { display: flex; flex-wrap: wrap; margin: 0; }
            .col-md-6 { flex: 0 0 50%; max-width: 50%; padding: 0 3px; }
            .col-md-4 { flex: 0 0 33.33%; max-width: 33.33%; padding: 0 3px; }
            .info-card { 
              border: 1px solid black; 
              padding: 10px; 
              margin-bottom: 10px; 
              background: white !important;
              border-left: 3px solid #0A2634 !important;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
              min-height: 80px;
              page-break-inside: avoid;
            }
            .supplier-card {
              border-left-color: #2D3748 !important;
            }
            .stats-card {
              border-left-color: #0A2634 !important;
            }
            .info-header {
              font-size: 11px;
              font-weight: bold;
              color: black !important;
              margin-bottom: 8px;
              border-bottom: 1px solid #ccc;
              padding-bottom: 3px;
              page-break-after: avoid;
            }
            .info-item, .stat-row {
              margin-bottom: 5px;
              font-size: 9px;
              color: black !important;
              page-break-inside: avoid;
            }
            .info-label, .stat-label {
              font-weight: bold;
              color: black !important;
              min-width: 80px;
              display: inline-block;
            }
            .stat-value {
              font-weight: bold;
              color: #0A2634 !important;
            }
            .summary-table-container, .table-container {
              border: 1px solid black;
              margin-bottom: 12px;
              background: white !important;
              /* Removed page-break-inside: avoid to allow table breaking */
            }
            .modern-summary-table, .modern-orders-table {
              width: 100%;
              border-collapse: collapse;
              font-size: 8px;
            }
            .modern-summary-table th {
              background: black !important;
              color: white !important;
              padding: 5px 3px;
              border: 1px solid black;
              font-weight: bold;
              font-size: 8px;
              text-align: center;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
              page-break-after: avoid;
            }
            .modern-summary-table td, .modern-orders-table td {
              padding: 4px 3px;
              border: 1px solid black;
              color: black !important;
              font-size: 8px;
              text-align: center;
              page-break-inside: avoid; /* Prevent individual rows from breaking */
            }
            .modern-orders-table th {
              background: #f0f0f0 !important;
              color: black !important;
              padding: 4px 3px;
              border: 1px solid black;
              font-weight: bold;
              font-size: 7px;
              text-align: left;
              page-break-after: avoid;
            }
            .modern-orders-table tr {
              page-break-inside: avoid; /* Prevent table rows from breaking */
            }
            .stat-badge {
              background: none !important;
              color: black !important;
              border: 1px solid black !important;
              padding: 1px 4px;
              font-size: 8px;
              font-weight: bold;
            }
            .amount {
              font-weight: bold;
              color: black !important;
            }
            .total-amount {
              font-weight: bold;
              color: #0A2634 !important;
              font-size: 9px;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }
            .analytics-item {
              background: #f9f9f9 !important;
              border-left: 3px solid #0A2634 !important;
              padding: 5px;
              margin-bottom: 5px;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
              font-size: 8px;
              page-break-inside: avoid;
            }
            .analytics-label {
              font-weight: bold;
              color: black !important;
            }
            .analytics-value {
              color: #0A2634 !important;
              font-weight: bold;
            }
            .notes-content {
              background: #f9f9f9 !important;
              border-left: 3px solid #0A2634 !important;
              padding: 8px;
              font-size: 9px;
              color: black !important;
              page-break-inside: avoid;
            }
            .signature-section {
              margin-top: 15px;
              page-break-inside: avoid;
            }
            .signature-title {
              font-weight: bold;
              color: black !important;
              margin-bottom: 8px;
              font-size: 10px;
              text-align: center;
              page-break-after: avoid;
            }
            .signature-line {
              height: 40px;
              border-bottom: 2px solid black;
              margin-bottom: 3px;
              background: white !important;
            }
            .signature-date {
              text-align: center;
              font-size: 8px;
              color: black !important;
            }
            .document-footer {
              margin-top: 15px;
              padding-top: 8px;
              border-top: 1px solid black;
              text-align: center;
              font-size: 7px;
              color: black !important;
              page-break-inside: avoid;
            }
            .ml-2, .badge:not(.stat-badge) {
              display: none !important;
            }
            .company-info-section {
              page-break-inside: avoid;
            }
            .summary-section {
              page-break-inside: avoid;
            }
            .orders-section {
              /* Allow orders section to break across pages */
            }
            .analytics-section {
              margin-bottom: 6px;
              page-break-inside: avoid;
            }
            .notes-section {
              margin-bottom: 6px;
              page-break-inside: avoid;
            }
            .location-info .location-item {
              page-break-inside: avoid;
            }
            .phone-info {
              page-break-inside: avoid;
            }
          }
        </style>
      `

      document.head.insertAdjacentHTML('beforeend', printCSS)
      window.print()
      setTimeout(() => {
        const printStyleElement = document.getElementById('print-styles')
        if (printStyleElement) {
          printStyleElement.remove()
        }
      }, 1000)
    },
    printPickupBW() {
      // Ultra-compact B&W print CSS for minimal ink and paper efficiency (enhanced: thinner borders, less bold, less padding)

      const printCSS = `
        <style id="print-styles-bw">
          @page {
            margin: 0 !important;
          }
          @media print {
            html, body {
              margin: 0 !important;
              padding: 0 !important;
              font-family: Arial, sans-serif;
              font-size: 7px;
              line-height: 1.05;
              color: #000;
              background: #fff !important;
              min-height: 0 !important;
              height: auto !important;
            }
            .pickup-preview-wrapper {
              margin: 0 !important;
              padding: 0 !important;
              min-height: 0 !important;
              height: auto !important;
            }
            .pickup-content {
              margin: 0 !important;
              padding: 0 !important;
            }
            body * { visibility: hidden; }
            #pickup-content, #pickup-content * { visibility: visible; }
            #pickup-content {
              position: absolute;
              left: 0;
              top: 0;
              width: 100%;
              font-size: 7px;
              color: #000;
              background: #fff !important;
              margin-top: 0 !important;
              padding-top: 0 !important;
            }
            .pickup-preview-wrapper, .pickup-content, .pickup-card, .pickup-header, .company-logo, .document-branding {
              margin-top: 0 !important;
              padding-top: 0 !important;
              margin-bottom: 0 !important;
              padding-bottom: 0 !important;
            }
            .d-print-none, .feather-icon, .no-print { display: none !important; }
            .pickup-header {
              margin-top: 0 !important;
              margin-bottom: 1px;
              padding: 1px 1px 0.5px 1px;
              background: #fff !important;
              color: #000 !important;
              border: 0.25px solid #000;
              page-break-inside: avoid;
            }
            .logo-img {
              display: none !important;
            }
            .company-name {
              font-size: 10px;
              font-weight: bold;
              color: #000 !important;
              margin-bottom: 2px;
            }
            .document-title {
              font-size: 8px;
              font-weight: bold;
              color: #000 !important;
              margin-bottom: 1px;
            }
            .company-tagline {
              color: #000 !important;
              font-size: 7px;
              margin-bottom: 0;
            }
            .pickup-title {
              font-size: 10px;
              font-weight: bold;
              color: #000 !important;
              margin-bottom: 2px;
            }
            .pickup-number, .pickup-date {
              color: #000 !important;
              font-size: 8px;
              font-weight: bold;
            }
            .section-title {
              font-size: 8px;
              font-weight: bold;
              border-bottom: 0.5px solid #000;
              margin: 2px 0 2px 0;
              padding-bottom: 1px;
              color: #000 !important;
              page-break-after: avoid;
            }
            .row { display: flex; flex-wrap: wrap; margin: 0; }
            .col-md-6 { flex: 0 0 50%; max-width: 50%; padding: 0 0.25px; }
            .col-md-4 { flex: 0 0 33.33%; max-width: 33.33%; padding: 0 0.25px; }
            .info-card {
              border: 0.5px solid #000;
              padding: 1px;
              margin-bottom: 1px;
              background: #fff !important;
              min-height: 10px;
              page-break-inside: avoid;
            }
            .info-header {
              font-size: 7px;
              font-weight: bold;
              color: #000 !important;
              margin-bottom: 1px;
              border-bottom: 0.5px solid #000;
              padding-bottom: 1px;
            }
            .info-item, .stat-row {
              margin-bottom: 0.5px;
              font-size: 6px;
              color: #000 !important;
            }
            .info-label, .stat-label {
              font-weight: bold;
              color: #000 !important;
              min-width: 30px;
              display: inline-block;
            }
            .stat-value {
              font-weight: bold;
              color: #000 !important;
            }
            .summary-table-container, .table-container {
              border: 0.5px solid #000;
              margin-bottom: 1px;
              background: #fff !important;
              /* Allow tables to break across pages */
              page-break-inside: auto !important;
            }
            .modern-summary-table, .modern-orders-table {
              width: 100%;
              border-collapse: collapse;
              font-size: 6px;
            }
            .modern-summary-table th, .modern-orders-table th {
              background: #fff !important;
              color: #000 !important;
              padding: 1px;
              border: 0.5px solid #000;
              font-weight: bold;
              font-size: 6px;
              text-align: center;
              page-break-after: avoid !important;
            }
            .modern-summary-table td, .modern-orders-table td {
              padding: 1px;
              border: 0.5px solid #000;
              color: #000 !important;
              font-size: 5px;
              text-align: center;
              page-break-inside: avoid !important;
            }
            .modern-orders-table tr, .modern-summary-table tr {
              page-break-inside: avoid !important;
            }
            .section-title, .section-header {
              page-break-after: avoid !important;
            }
            .company-info-section, .summary-section, .signature-section, .totals-footer {
              page-break-inside: avoid !important;
            }
            .stat-badge {
              background: #fff !important;
              color: #000 !important;
              border: 0.5px solid #000 !important;
              padding: 0 1px;
              font-size: 5px;
              font-weight: bold;
            }
            .amount, .total-amount {
              font-weight: bold;
              color: #000 !important;
              font-size: 6px;
            }
            .signature-section {
              margin-top: 2px;
              page-break-inside: avoid;
            }
            .signature-title {
              font-weight: bold;
              color: #000 !important;
              margin-bottom: 1px;
              font-size: 7px;
              text-align: center;
            }
            .signature-line {
              height: 10px;
              border-bottom: 0.5px solid #000;
              margin-bottom: 1px;
              background: #fff !important;
            }
            .document-footer {
              margin-top: 1px;
              padding-top: 1px;
              border-top: 0.5px solid #000;
              text-align: center;
              font-size: 5px;
              color: #000 !important;
              page-break-inside: avoid;
            }
            .ml-2, .badge:not(.stat-badge) {
              display: none !important;
            }
            .company-info-section, .summary-section, .signature-section, .totals-footer {
              page-break-inside: avoid !important;
            }
            * {
              transition: none !important;
              animation: none !important;
              transform: none !important;
            }
          }
        </style>
      `

      document.head.insertAdjacentHTML('beforeend', printCSS)
      window.print()
      setTimeout(() => {
        const printStyleElement = document.getElementById('print-styles-bw')
        if (printStyleElement) {
          printStyleElement.remove()
        }
      }, 1000)
    },

    // Confirmation methods
    canConfirm(pickup = this.pickup) {
      // Only suppliers can confirm
      if (this.user.role !== 'supplier') {
        return false
      }

      // Only pickup owner can confirm
      if (pickup.supplier?.id !== this.user.id) {
        return false
      }

      // Only pending pickups can be confirmed
      return pickup.status === 'pending'
    },

    showConfirmDialog() {
      this.showConfirmationModal = true
    },

    cancelConfirmation() {
      this.showConfirmationModal = false
    },

    async confirmPickup() {
      if (!this.pickup.id) return

      this.isConfirming = true

      try {
        const response = await axios.post(`/api/pickups/confirm/${this.pickup.id}/`)

        this.$bvToast.toast(response.data.message || 'Pickup confirmé avec succès', {
          title: 'Succès',
          variant: 'success',
          solid: true,
        })

        // Refresh the pickup data
        await this.fetchPickupDetails()

        // Close modal
        this.showConfirmationModal = false
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error confirming pickup:', error)

        let errorMessage = 'Erreur lors de la confirmation du pickup'
        if (error.response?.status === 400) {
          errorMessage = error.response.data?.detail || 'Ce pickup ne peut pas être confirmé'
        } else if (error.response?.status === 404) {
          errorMessage = 'Pickup introuvable'
        } else if (error.response?.data?.detail) {
          errorMessage = error.response.data.detail
        }

        this.$bvToast.toast(errorMessage, {
          title: 'Erreur',
          variant: 'danger',
          solid: true,
        })
      } finally {
        this.isConfirming = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
// Brand Colors - MZ Logistic
$brand-primary: #0A2634;
$brand-secondary: #2D3748;
$brand-light: #e8f4f8;
$brand-dark: #1A202C;

// Remove top padding and minimize vertical gap for ultra-compact layout
.pickup-preview-wrapper {
  padding: 0.25rem 0.5rem 0.5rem 0.5rem;
  background: linear-gradient(135deg, #f7fafc 0%, #edf2f7 100%);
  min-height: 100vh;
  transform: translateZ(0);
  will-change: transform;
}

// Force proper rendering on navigation
.pickup-preview-wrapper * {
  box-sizing: border-box;
}

.top-action-bar {
  background: white;
  padding: 1.5rem;
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  border-left: 4px solid $brand-primary;
  // Ensure proper positioning
  position: relative;
  z-index: 1;
}

.breadcrumb-section {
  .modern-breadcrumb {
    background: transparent;
    margin: 0;
    padding: 0;
  }
}

.page-title {
  color: $brand-secondary;
  font-weight: 700;
  display: flex;
  align-items: center;
}

.action-buttons-top {
  display: flex;
  align-items: center;
  justify-content: flex-end;

  @media (max-width: 768px) {
    justify-content: flex-start;
    margin-top: 1rem;
  }
}

.action-btn-top {
  border-radius: 8px;
  font-weight: 600;
  padding: 0.75rem 1.5rem;
  transition: all 0.3s ease;
  margin-left: 0.5rem;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }

  &.btn-success {
    background: linear-gradient(135deg, #28a745 0%, #1e7e34 100%);
    border-color: #28a745;

    &:hover {
      background: linear-gradient(135deg, #1e7e34 0%, #155724 100%);
      border-color: #1e7e34;
    }
  }

  &.dropdown .btn {
    border-color: $brand-primary;
    color: $brand-primary;
    background-color: transparent;

    &:hover, &:focus {
      background-color: rgba(10, 38, 52, 0.1) !important;
      border-color: $brand-primary !important;
      color: $brand-primary !important;
    }
  }

  .dropdown-menu {
    border-radius: 8px;
    border: 1px solid rgba(10, 38, 52, 0.2);
    box-shadow: 0 8px 25px rgba(10, 38, 52, 0.15);

    .dropdown-item {
      padding: 0.75rem 1rem;
      font-weight: 500;
      transition: all 0.2s ease;

      &:hover {
        background-color: rgba(10, 38, 52, 0.08) !important;
        color: $brand-primary !important;
      }
    }
  }
}

.quick-stats {
  @media (max-width: 768px) {
    margin-left: 0 !important;
    margin-top: 0.5rem;
  }
}

.stat-item-inline {
  display: flex;
  align-items: center;
  padding: 0.5rem 0.75rem;
  margin-left: 0.5rem;
  border-radius: 20px;
  font-size: 0.875rem;
  font-weight: 600;

  svg {
    margin-right: 0.25rem;
  }

  &.primary {
    background: rgba(10, 38, 52, 0.1);
    color: $brand-primary;
  }

  &.success {
    background: rgba(72, 187, 120, 0.1);
    color: #48bb78;
  }

  &.warning {
    background: rgba(237, 137, 54, 0.1);
    color: #ed8936;
  }
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
}

.pickup-card {
  border-radius: 8px;
  border: 1px solid #eee;
  overflow: hidden;
  background: #fff;
  animation: fadeInUp 0.6s ease-out;
  position: relative;
  z-index: 1;
  transform: translateZ(0);
  margin-top: 0 !important;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.pickup-header {
  padding: 1.5rem 2rem;
  background: #fff5f5;
  border-radius: 12px 12px 0 0;
  margin-bottom: 2rem;

  .logo-img {
    max-height: 50px;
    width: auto;
  }

  .text-right {
    text-align: right;
  }

  .pickup-title {
    color: #333;
    font-size: 1.5rem;
    font-weight: 800;
    margin: 0;

    .pickup-number {
      color: #ED1919;
    }
  }

  .pickup-date {
    color: #666;
    font-size: 0.9rem;
    margin-top: 0.25rem;
    display: flex;
    align-items: center;
    justify-content: flex-end;
  }
}

.company-logo {
  display: inline-block;
  background: transparent;
  padding: 0;
  border-radius: 0;
  box-shadow: none;
}

.company-logo .logo-img {
  max-width: 100px;
  height: auto;
  margin-top: 0;
  margin-bottom: 0;
  display: block;
}

.document-branding {
  .company-name {
    font-size: 1.1rem;
    font-weight: 700;
    margin-bottom: 0.1rem;
  }

  .document-title {
    font-size: 0.95rem;
    font-weight: 600;
    margin-bottom: 0.1rem;
  }

  .company-tagline {
    opacity: 0.9;
    font-size: 0.7rem;
    margin-bottom: 0;
  }
}

.pickup-title {
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
  padding-right: 2.5rem;
}

.pickup-number {
  color: $brand-light;
}

.pickup-date {
  font-size: 1rem;
  opacity: 0.9;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding-right: 2.5rem;
}

.status-container {
  text-align: right;
  padding-right: 2.5rem;

  @media (max-width: 768px) {
    text-align: center;
  }
}

.company-info-section {
  margin-bottom: 2rem;
}

.info-card {
  background: #f7fafc;
  border-radius: 12px;
  padding: 1.5rem;
  height: 100%;
  border-left: 4px solid $brand-primary;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
  }
}

.supplier-card {
  border-left-color: $brand-secondary;
}

.stats-card {
  border-left-color: $brand-primary;
}

.info-header {
  display: flex;
  align-items: center;
  margin-bottom: 1rem;
  color: $brand-secondary;
  font-weight: 600;

  h5 {
    margin: 0;
  }
}

.info-content .info-item,
.stats-content .stat-row {
  margin-bottom: 0.75rem;
  display: flex;
  justify-content: space-between;
  align-items: center;

  .info-label,
  .stat-label {
    font-weight: 600;
    color: $brand-secondary;
  }

  .stat-value {
    font-weight: 700;

    &.primary { color: $brand-primary; }
    &.success { color: #28a745; }
    &.info { color: #17a2b8; }
    &.warning { color: #ffc107; }
  }
}

.summary-section,
.orders-section,
.analytics-section,
.notes-section {
  margin-bottom: 2rem;
}

.section-title {
  display: flex;
  align-items: center;
  font-weight: 600;
  color: $brand-secondary;
  margin-bottom: 1rem;
}

.summary-table-container,
.table-container {
  background: #fff;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  // Force proper table rendering
  position: relative;
  z-index: 1;

  // Ensure tables render properly on navigation
  table {
    table-layout: fixed;
    width: 100%;
  }
}

.modern-summary-table {
  width: 100%;
  margin-bottom: 0;
  // Force table layout recalculation
  table-layout: fixed;

  thead th {
    background: black !important;
    color: white !important;
    padding: 1rem 0.75rem;
    font-weight: 600;
    border: none;
    text-align: center;
    font-size: 0.875rem;
    // Ensure header stays in place
    position: relative;
    z-index: 2;
  }

  tbody td {
    padding: 1rem 0.75rem;
    text-align: center;
    border-bottom: 1px solid #e2e8f0;
    // Force cell rendering
    position: relative;
  }
}

.modern-orders-table {
  margin-bottom: 0;
  // Force table layout recalculation
  table-layout: fixed;

  thead th {
    background: #f7fafc;
    border-bottom: 2px solid #e2e8f0;
    font-weight: 600;
    color: $brand-secondary;
    padding: 1rem 0.75rem;
    // Ensure header stays in place
    position: relative;
    z-index: 2;
  }

  tbody tr {
    transition: all 0.2s ease;
    // Force row rendering
    position: relative;

    &:hover {
      background-color: #f7fafc;
    }

    td {
      padding: 0.75rem;
      border-color: #edf2f7;
      // Force cell rendering
      position: relative;
    }
  }
}

.stat-badge {
  font-size: 1rem;
  padding: 0.5rem 0.75rem;
  font-weight: 600;
  // Ensure we override bootstrap variants
  background-color: rgba(237, 25, 25, 0.1) !important;
  color: #ED1919 !important;

  &.badge {
    background-color: rgba(237, 25, 25, 0.1) !important;
    color: #ED1919 !important;
  }
}

.amount {
  font-weight: 600;
  color: $brand-secondary;
}

.total-amount {
  font-size: 1.125rem;
  color: $brand-primary;
  font-weight: 700;
}

.order-code strong {
  color: $brand-secondary;
}

.no-orders-state {
  text-align: center;
  padding: 3rem 2rem;
  color: #718096;
}

.analytics-content {
  background: #fff;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
}

.analytics-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;
}

.analytics-item {
  background: #f7fafc;
  padding: 1rem 1.25rem;
  border-radius: 8px;
  border-left: 4px solid $brand-primary;
  display: flex;
  justify-content: space-between;
  align-items: center;

  .analytics-label {
    font-weight: 600;
    color: $brand-secondary;
  }

  .analytics-value {
    font-weight: 600;
    color: $brand-primary;
  }
}

.notes-content {
  background: #fff;
  padding: 1.5rem;
  border-radius: 12px;
  border-left: 4px solid $brand-primary;
  font-size: 1rem;
  line-height: 1.6;
  color: $brand-secondary;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  font-style: italic;
}

.signature-section {
  margin-top: 2rem;
}

.signature-box {
  text-align: center;
  padding: 1rem;
  background: #f7fafc;
  border-radius: 8px;
  border: 1px dashed #718096;
}

.signature-title {
  font-weight: 600;
  color: $brand-secondary;
  margin-bottom: 1rem;
  font-size: 0.9rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.signature-line {
  height: 60px;
  border-bottom: 2px solid $brand-secondary;
  margin-bottom: 0.5rem;
  background: white;
  border-radius: 4px;
}

.signature-date {
  font-size: 0.8rem;
  color: #718096;
  font-weight: 500;
}

.document-footer {
  margin-top: 2rem;
  padding: 1.5rem;
  background: #f7fafc;
  border-radius: 8px;
  border-top: 3px solid $brand-primary;
  text-align: center;
}

.print-info {
  color: #718096;
}

.print-date {
  font-size: 0.9rem;
  margin-bottom: 0.5rem;
  font-weight: 600;
}

.print-note {
  font-size: 0.8rem;
  font-style: italic;
  margin: 0;
}

.status-badge {
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-weight: 600;
  font-size: 0.9rem;

  &.badge-warning {
    background-color: rgba(237, 25, 25, 0.1) !important;
    color: white !important;
  }
}

// Enhanced styling for the redesigned company info section
.company-info-section {
  .supplier-info-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15) !important;
  }

  .stats-info-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(10, 38, 52, 0.15) !important;
  }
}

// Responsive adjustments
@media (max-width: 768px) {
  .company-info-section {
    div[style*="display: flex"] {
      flex-direction: column !important;
    }

    div[style*="min-width: 300px"] {
    min-width: 100% !important;
    }
  }
}

  // New Card Styles
  .company-card, .supplier-card {
    background: #fcfcfc;
    border-radius: 12px;
    padding: 1.5rem;
    border-left: 4px solid #ED1919;
    box-shadow: 0 2px 10px rgba(0,0,0,0.05);

    .card-header-custom {
      color: #333;
    }
  }

// Print-specific styles for the redesigned section
@media print {
  .company-info-section {
    page-break-inside: avoid !important;

    .supplier-info-card,
    .stats-info-card {
      border: 1px solid black !important;
      background: white !important;
      box-shadow: none !important;
      transform: none !important;

      h5 {
        color: black !important;
      }

      span {
        color: black !important;
      }
    }

    .supplier-info-card {
      border-left: 3px solid black !important;
    }

    .stats-info-card {
      border-left: 3px solid black !important;
    }
  }
}
</style>
